import {
  type DistanceUnit as CoreDistanceUnit,
  convert,
  labelFor,
  type ResolvedUnits,
} from '@brightdrop/localization-core';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import AlertIcon from '~/assets/icons/new/alert.svg?react';
import GoodIcon from '~/assets/icons/new/checkCircle.svg?react';
import ErrorIcon from '~/assets/icons/new/error.svg?react';
import FuelIcon from '~/assets/icons/new/fuelIcon.svg?react';
import type { BDFilterModalOptionConfig } from '~/common/components/BDFilter/BDFilterModal';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';

import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import {
  type DiagnosticValueFormat,
  formatChargeReading,
  percentLabel,
  readMountedFormatBundle,
} from '~/common/localization/diagnosticFormat';
import type { BatteryRange } from '~/common/models/asset.model';
import { AssetReportType } from '~/common/models/asset.model';
import {
  type BatteryChargingStatus,
  type EstimatedRangedInKM,
  ReportDiagnosticState,
  type VehicleDetailsType,
} from '~/common/models/asset-report.model';
import type { ConnectivityStatusType } from '~/common/models/common.model';
import { isEstCompletionTimeDisplayValid } from '~/common/utils/battery.utils';
import { formatCompletionDateToTimezone } from '~/common/utils/date-time.formatter.utils';
import { formatDuration } from '~/common/utils/date-time.utils';
import type { AssetListFilterCriteria } from '../../assetsSlice';
import type { ASSET_LIST_REPORT_FILTER_MESSAGES } from '../AssetListReportViewFilterModal.messages';
import {
  getBatteryFilterModalConfig,
  getFluidsFilterModalConfig,
  getReadinessFilterModalConfig,
  getRecallsFilterModalConfig,
  getTiresFilterModalConfig,
} from '../configs/AssetListReportViewFilter.config';

export const getReportFilterModalColumnsConfig = (
  selectedReport: AssetReportType,
  filterLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.filterLabels,
  valueLabels: typeof ASSET_LIST_REPORT_FILTER_MESSAGES.valueLabels,
  hideAvailabilityFilter: boolean,
  units: ResolvedUnits,
  locale: string,
  isNotEquippedFeatureFlag: boolean
): BDFilterModalOptionConfig<AssetListFilterCriteria>[] => {
  switch (selectedReport) {
    case AssetReportType.FLEET_READINESS:
      return getReadinessFilterModalConfig(
        filterLabels,
        valueLabels,
        hideAvailabilityFilter,
        isNotEquippedFeatureFlag
      );
    case AssetReportType.FLUIDS:
      return getFluidsFilterModalConfig(
        filterLabels,
        valueLabels,
        hideAvailabilityFilter
      );
    case AssetReportType.BATTERY:
      return getBatteryFilterModalConfig(
        filterLabels,
        hideAvailabilityFilter,
        units,
        locale,
        isNotEquippedFeatureFlag
      );
    case AssetReportType.TIRE_PRESSURE:
      return getTiresFilterModalConfig(filterLabels, valueLabels);
    case AssetReportType.RECALLS:
      return getRecallsFilterModalConfig();
    default:
      return getReadinessFilterModalConfig(
        filterLabels,
        valueLabels,
        hideAvailabilityFilter,
        isNotEquippedFeatureFlag
      );
  }
};

export const formatLastDateCommunicated = (
  lastCommunication?: string,
  unitDisplay: 'short' | 'long' | 'narrow' = 'short',
  userLocale?: string
): string | undefined => {
  const offlineThreshold = DateTime.now().toJSDate();
  if (
    !lastCommunication ||
    new Date(lastCommunication).getTime() >= offlineThreshold.getTime()
  ) {
    return undefined;
  }

  const duration = formatDuration(
    offlineThreshold,
    new Date(lastCommunication),
    userLocale,
    unitDisplay
  );

  // If duration is '0m', the device is essentially online, return undefined
  return duration === '0m' ? undefined : duration;
};

/**
 * Formats estimated completion time. Checks that charging status is appropriate for estimated completion time.
 */
export const formatEstCompletionTime = (
  chargingStatus: BatteryChargingStatus,
  date?: string,
  locale?: string,
  timeZone?: string
) => {
  const validEvChargeStateForCompletion =
    isEstCompletionTimeDisplayValid(chargingStatus);
  const emptyDateValue = !date || typeof date !== 'string' || date === '';

  if (validEvChargeStateForCompletion && !emptyDateValue) {
    return formatCompletionDateToTimezone(date, locale, timeZone);
  }

  return DEFAULT_EMPTY_VALUE;
};

export const getDiagnosticModalLabel = <T extends string>(
  FILTER_MODAL_RANGE: {
    [k in T]: {
      start?: number;
      end?: number;
      inclusiveStart?: boolean;
      inclusiveEnd?: boolean;
    };
  },
  status: T
) => {
  const range = FILTER_MODAL_RANGE[status];
  if (range?.start && range?.end) {
    return `${percentLabel(range.start, readMountedFormatBundle())} - ${percentLabel(range.end, readMountedFormatBundle())}`;
  }
  if (range?.start && !range?.end) {
    return `> ${percentLabel(range.start, readMountedFormatBundle())}`;
  }
  if (!range?.start && range?.end) {
    return `< ${percentLabel(range.end, readMountedFormatBundle())}`;
  }
  return '';
};
export const getFuelLevelDiagnosticStatus = (
  testId: string,
  status?: ReportDiagnosticState,
  value?: string
): ReactElement => {
  switch (status) {
    case ReportDiagnosticState.GOOD:
    case ReportDiagnosticState.IMPAIRED:
    case ReportDiagnosticState.CRITICAL:
      return (
        <Box
          data-testid={`${status}-diagnostic-status`}
          display="flex"
          alignItems="center"
        >
          <FuelIcon
            className={`fuel-level-${status}-diagnostic-color`}
            data-testid={`vehicle-list-diagnostic-${testId}-status-icon`}
          />
          {value && (
            <Box
              className={'valueStatus'}
              component="span"
              pl="4px"
              data-testid={`fuel-level-diagnostic-${status}`}
            >
              {value}
            </Box>
          )}
        </Box>
      );

    case ReportDiagnosticState.NO_DATA:
      if (value && value !== DEFAULT_EMPTY_VALUE) {
        return (
          <Box data-testid="no-data-diagnostic-status" className="valueStatus">
            {value}
          </Box>
        );
      }
      return (
        <Box data-testid="no-data-diagnostic-status">{DEFAULT_EMPTY_VALUE}</Box>
      );
    case ReportDiagnosticState.UNKNOWN:
    case ReportDiagnosticState.CLEAR:
      return (
        <Box data-testid="no-data-diagnostic-status">{DEFAULT_EMPTY_VALUE}</Box>
      );
    default:
      return (
        <Box data-testid="no-data-diagnostic-status">{DEFAULT_EMPTY_VALUE}</Box>
      );
  }
};
export const getReportDiagnosticStatus = (
  testId: string,
  status?: ReportDiagnosticState,
  value?: string
): ReactElement => {
  switch (status) {
    case ReportDiagnosticState.GOOD:
      return (
        <Box
          data-testid={`${status}-diagnostic-status`}
          display="flex"
          alignItems="center"
        >
          <GoodIcon
            className={`${status}-diagnostic-color`}
            data-testid={`vehicle-list-diagnostic-${testId}-status-icon`}
          />
          {value && (
            <Box
              component="span"
              pl="4px"
              data-testid={`vehicle-list-diagnostic-${testId}-diagnostic-status`}
            >
              {value}
            </Box>
          )}
        </Box>
      );
    case ReportDiagnosticState.IMPAIRED:
      return (
        <Box
          data-testid={`${status}-diagnostic-status`}
          display="flex"
          alignItems="center"
        >
          <AlertIcon className={`${status}-diagnostic-color`} />
          {value && (
            <Box component="span" ml="5px">
              {value}
            </Box>
          )}
        </Box>
      );
    case ReportDiagnosticState.CRITICAL:
      return (
        <Box
          data-testid={`${status}-diagnostic-status`}
          display="flex"
          alignItems="center"
        >
          <ErrorIcon className={`${status}-diagnostic-color`} />
          {value && (
            <Box component="span" ml="5px">
              {value}
            </Box>
          )}
        </Box>
      );
    case ReportDiagnosticState.NO_DATA:
      if (value && value !== DEFAULT_EMPTY_VALUE) {
        return (
          <Box data-testid="no-data-diagnostic-status" className="valueStatus">
            {value}
          </Box>
        );
      }
      return (
        <Box data-testid="no-data-diagnostic-status">{DEFAULT_EMPTY_VALUE}</Box>
      );
    case ReportDiagnosticState.UNKNOWN:
    case ReportDiagnosticState.CLEAR:
      return (
        <Box data-testid="no-data-diagnostic-status">{DEFAULT_EMPTY_VALUE}</Box>
      );
    default:
      return (
        <Box data-testid="no-data-diagnostic-status">{DEFAULT_EMPTY_VALUE}</Box>
      );
  }
};

export const getConnectivityBoardingStatusStatus = (
  status: ConnectivityStatusType | undefined,
  value: string | undefined
): ReactElement =>
  status ? (
    <Box
      data-testid={`vehicle-list-connectivity-status-${status}`}
      display="flex"
      alignItems="center"
    >
      <Box>
        <Box className={`dot ${status}-connectivity-status`} />
      </Box>
      {value && (
        <Box ml="3px" width={'100px'}>
          {value}
        </Box>
      )}
    </Box>
  ) : (
    <Box data-testid={'vehicle-list-connectivity-status-undefined'}>
      {DEFAULT_EMPTY_VALUE}
    </Box>
  );
export const getVehicleTypeDetails = (type: VehicleDetailsType) => {
  const { year, make, model } = type;
  const values = [year, make, model];
  return values
    .filter((value) => value !== null && value !== undefined)
    .join(' ');
};

export const getRangeFilterModalLabel = (
  range: {
    start?: number;
    end?: number;
    inclusiveStart?: boolean;
    inclusiveEnd?: boolean;
  },
  symbol: string
) => {
  if (range?.start && range?.end) {
    return `${range?.start}${symbol} - ${range?.end}${symbol}`;
  }
  if (range?.start && !range?.end) {
    return `${range.start}${symbol} ${translateText('asset:orMore')}`;
  }
  if (!range?.start && range?.end) {
    return `${range.end}${symbol} ${translateText('asset:orLess')}`;
  }
  return '';
};

export const getDiagnosticsFilterModalLabel = <T extends string>(
  FILTER_MODAL_RANGE: {
    [k in T]: {
      start?: number;
      end?: number;
      inclusiveStart?: boolean;
      inclusiveEnd?: boolean;
    };
  },
  status: T
) => {
  const range = FILTER_MODAL_RANGE[status];
  if (range?.start && range?.end) {
    return `${percentLabel(range.start, readMountedFormatBundle())} - ${percentLabel(range.end, readMountedFormatBundle())}`;
  }
  if (range?.start && !range?.end) {
    return `${percentLabel(range.start, readMountedFormatBundle())} ${translateText('asset:orMore')}`;
  }
  if (!range?.start && range?.end) {
    return `${percentLabel(range.end, readMountedFormatBundle())} ${translateText('asset:orLess')}`;
  }
  return '';
};

export const getEstimatedRangeFilterModalLabel = <T extends string>(
  FILTER_MODAL_RANGE: {
    [k in T]: {
      start?: number;
      end?: number;
      inclusiveStart?: boolean;
      inclusiveEnd?: boolean;
    };
  },
  status: T,
  unitOfMeasure: CoreDistanceUnit,
  displayUnit: CoreDistanceUnit,
  locale: string
) => {
  const range = FILTER_MODAL_RANGE[status];
  const format = readMountedFormatBundle();
  const calcValue = (val: number | undefined) => {
    if (!val) {
      return val;
    }
    if (format) {
      return Math.round(format.distance.raw(val, { valueUnit: unitOfMeasure }));
    }
    return Math.round(
      unitOfMeasure !== displayUnit
        ? convert('distance', val, unitOfMeasure, displayUnit)
        : val
    );
  };
  const startValue = calcValue(range?.start);
  const endValue = calcValue(range?.end);
  const symbol = format
    ? ` ${format.distance.label()}`
    : ` ${labelFor(locale, 'distance', displayUnit)}`;
  return getRangeFilterModalLabel(
    {
      start: startValue ?? undefined,
      end: endValue ?? undefined,
      inclusiveStart: range?.inclusiveStart,
      inclusiveEnd: range?.inclusiveEnd,
    },
    symbol
  );
};

/**
 * Formats the battery state of charge with range information if available
 * @param units - The units configuration for display
 * @param battery - The battery data containing value, range category, and estimated range - not mandatory
 * @returns Formatted string with percentage and optional range%information.
 * Having estimated range - will display "80% (100km)",
 * If no range/estimated range provided will display just soc "80%".
 */
export const formatBatteryStateOfCharge = (
  _units: ResolvedUnits,
  battery:
    | {
        value?: number;
        range?: BatteryRange;
        estimatedRange?: EstimatedRangedInKM;
      }
    | undefined,
  format: DiagnosticValueFormat
): string => formatChargeReading(battery, format);
